import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import ArticleIcon from '@mui/icons-material/Article';
import DialogActions from '@mui/material/DialogActions';

import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ChromeReaderModeIcon from '@mui/icons-material/ChromeReaderMode';
import ArchitectureIcon from '@mui/icons-material/Architecture';

// Custom styled components
const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  minHeight: '280px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px 16px',
  cursor: 'pointer',
  backgroundColor: '#fff',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    color:"white",
    '& .hover-overlay': {
      transform: 'translateY(0)',
      color:"white"
    }
  }
}));

const HoverOverlay = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '100%',
  backgroundColor: '#172554',
  color:"white",
  transform: 'translateY(100%)',
  transition: 'transform 0.3s ease-in-out',
  zIndex: 1,
});

const IconWrapper = styled(Box)({
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  '& svg': {
    fontSize: '32px',
    color: '#172554',
  }
});

const LearnMoreButton = styled(Button)({
  marginTop: 'auto',
  color: '#2196f3',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  }
});

interface ServiceCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  fullDescription: string;
}

const services: ServiceCard[] = [
{
    id: 'dissertation',
    title: 'Dissertation',
    description:
      'Our company offers expert assistance for dissertation writing, including topic selection, research guidance, and thorough Project writing services. .',
    icon: <ArticleIcon />,
    fullDescription:
      'Our expert team provides comprehensive dissertation writing services, covering topic selection, research methodology, and editing..',
  },
  {
    id: 'coursework',
    title: 'Coursework',
    description:
      'Our company provides comprehensive coursework support, from topic selection to final submission. We offer research assistance....',
    icon: <SchoolIcon />,
    fullDescription:
      'Our team offers end-to-end coursework support, including topic selection, research assistance, writing, and final edits. We ensure that every aspect of your coursework meets academic requirements and is delivered on time. Our personalized approach ensures you get the help you need to succeed.',
  },
  {
    id: 'data-analysis',
    title: 'Data Analysis',
    description:
      'Our company specializes in data analysis services, offering expert support in collecting, processing, and interpreting data for academic projects. ....',
    icon: <DataUsageIcon />,
    fullDescription:
      'We offer specialized data analysis services for academic research, using advanced statistical tools to collect, process, and interpret data. Our experts provide clear and actionable insights, helping you make data-driven decisions that enhance the quality and credibility of your research.',
  },
  {
    id: 'project-writing',
    title: 'Project Writing Services',
    description:
      'Our company offers professional Project writing services to refine your academic work with precision. We ensure clarity, coherence, and adherence to academic standards.....',
    icon: <AssignmentIcon />,
    fullDescription:
      'Our Project writing services focus on improving the clarity, structure, and coherence of your work. We ensure that your academic writing adheres to the highest standards, correcting grammar, formatting, and citation errors. Our team helps you deliver a polished and professional final submission.',
  },
  {
    id: 'publication',
    title: 'Publication',
    description:
      'Our company provides tailored Publication services designed to enhance your academic skills, covering areas such as research methods, writing techniques, ......',
    icon: <LibraryBooksIcon />,
    fullDescription:
      'Our Publication services are designed to help you develop the necessary skills to publish your research. From writing techniques to advanced .....',
  },
  {
    id: 'literature-review',
    title: 'Literature Review',
    description:
      'Our company offers specialized literature review services, helping you identify, analyze, and synthesize key academic sources relevant to your research. ....',
    icon: <SearchIcon />,
    fullDescription:
      'Our Literature Review services help you gather and analyze key academic sources relevant to your research topic. We provide a comprehensive and well-organized review.....',
  },
  {
    id: 'manuscript',
    title: 'Manuscript',
    description:
      'Our company provides expert manuscript services, assisting with everything from structuring to editing and polishing your academic work. We ensure clarity, ....',
    icon: <ChromeReaderModeIcon />,
    fullDescription:
      'Our manuscript services guide you through the entire preparation process, from structuring to final edits. We focus on ensuring clarity, coherence, and adherence to publication standards, helping you refine your manuscript for submission to journals or other academic platforms.',
  },
  {
    id: 'research-methodology',
    title: 'Research Methodology',
    description:
      'Our company offers expert research methodology services, assisting with the design and execution of research plans, data collection techniques, .....',
    icon: <ChromeReaderModeIcon />,
    fullDescription:
      'Our team helps you design effective research methodologies, including data collection techniques, analysis strategies, and statistical methods. We ensure your research plan is sound and meets academic standards, helping you achieve accurate and reliable results.',
  },
  // Add other services as necessary
];

const ServiceCardsGrid = () => {
  const [selectedCard, setSelectedCard] = useState<ServiceCard | null>(null);

  return (
    <Container maxWidth={false} sx={{ py: 8 }}>
      <Box id="services"  sx={{ textAlign: 'center', mb: 6 }}  >
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", color: '#172554', mb: 2 }} className='component'>
        Professional Assignment <span
              style={{
                background:
                  "linear-gradient(-96deg, rgb(194 183 183 / 0%) 0%, rgb(255 249 220 / 4%) 28%, rgb(255, 162, 49) 100%, rgb(255 255 255 / 0%) 100%)",
                padding: "0px 2px",
                borderLeft: "2px solid rgba(253,150,16,1)",
              }}
            >  Writing & Academic Support </span> Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" className='component'>
          Choose from our wide range of professional writing services
        </Typography>
      </Box>

      <Box className="component" sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 3,
      }} >
        {services.map((service) => (
          <StyledCard key={service.id} onClick={() => setSelectedCard(service)}>
            <HoverOverlay className="hover-overlay" />
            <Box  sx={{
              position: 'relative',
              zIndex: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <IconWrapper>
                {service.icon}
              </IconWrapper>
              <CardContent sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                p: 0,
              }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  {service.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} className='hover-overlay'>
                  {service.description}
                </Typography>
                <LearnMoreButton>
                  Learn more
                </LearnMoreButton>
              </CardContent>
            </Box>
          </StyledCard>
        ))}
      </Box>

      <Dialog open={!!selectedCard} onClose={() => setSelectedCard(null)} maxWidth="sm" fullWidth>
        {selectedCard && (
          <>
            <DialogTitle>{selectedCard.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {selectedCard.fullDescription}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedCard(null)} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ServiceCardsGrid;
